import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { EnvironmentBadge } from "@/components/common/badges";
import { CopyField } from "@/components/common/copy-button";
import { Field, Panel } from "@/components/common/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { q } from "@/data/queries";
import { formatDateTime } from "@/lib/format";

export const Route = createFileRoute("/p/$project/settings/")({
  component: SettingsGeneralPage,
});

function SettingsGeneralPage() {
  const { project: slug } = Route.useParams();
  const { data: projects } = useSuspenseQuery(q.projects());
  const { data: org } = useSuspenseQuery(q.organization());
  const project = projects.find((candidate) => candidate.slug === slug);
  if (!project) return null;

  return (
    <>
      <Panel title="Project" bodyClassName="flex flex-col gap-4 p-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-name">Name</Label>
            <Input id="project-name" defaultValue={project.name} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-slug">Slug</Label>
            <Input id="project-slug" defaultValue={project.slug} />
          </div>
        </div>
        <CopyField value={project.id} />
        <div>
          <Button size="sm" disabled>
            Save
          </Button>
        </div>
      </Panel>

      <Panel title="Environments" bodyClassName="px-4 py-2">
        <dl>
          {project.environments.map((environment) => (
            <Field
              key={environment}
              label={<EnvironmentBadge environment={environment} />}
            >
              <span className="text-muted-foreground">
                {environment === "production"
                  ? "Real accounts, real sends, metered and billed."
                  : "Fixture accounts that behave like the real ones. Sends go nowhere and nothing is billed."}
              </span>
            </Field>
          ))}
        </dl>
      </Panel>

      <Panel
        title="Data retention"
        description="What Aux0 keeps, and for how long"
        bodyClassName="px-4 py-2"
      >
        <dl>
          <Field label="Message content">
            30 days by default. Set it to zero and Aux0 keeps only ids, timestamps and
            the account the message belongs to.
          </Field>
          <Field label="Calendar events">
            Kept while the event is in the window a calendar syncs, plus 30 days.
            Cancelled events are kept as tombstones so a sync cannot resurrect them.
          </Field>
          <Field label="Webhook payloads">
            7 days, so a failed delivery can be replayed.
          </Field>
          <Field label="Audit log">90 days on Growth, one year on Scale.</Field>
          <Field label="Region">
            eu-west-1. Single-tenant deployments pick their own.
          </Field>
        </dl>
      </Panel>

      <Panel title="Organisation" bodyClassName="px-4 py-2">
        <dl>
          <Field label="Name">{org.name}</Field>
          <Field label="Created">{formatDateTime(org.createdAt)}</Field>
          <Field label="Plan">{org.plan}</Field>
        </dl>
      </Panel>
    </>
  );
}
